/**
 * OnboardingChat — Structured onboarding experience.
 * Phase 1: StateCheckIn (energy/certainty/clarity + breathing if needed)
 * Phase 2: Streaming chat with Navigator for discovery conversation
 * Auto-sends opening message, watches for complete_onboarding tool result.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader2, Compass } from 'lucide-react';
import { startOnboarding, sendMessageStream, sendMessage, setVoiceDefaultOn } from '../lib/api';
import { useVoiceConversation } from '../lib/useVoiceConversation';
import VoiceInput from './VoiceInput';
import { Volume2, VolumeX } from 'lucide-react';
import StateCheckIn from './StateCheckIn';
import { trackAuthed } from '../lib/analytics';
import { useHints } from '../lib/hints';
import DeepRestCard from './DeepRestCard';

export default function OnboardingChat({ onComplete, voiceEnabled = false, voiceId, voiceDefaultOn = false, freePlanningDays = null }) {
  const hints = useHints();
  const [phase, setPhase] = useState('intro'); // 'intro' | 'checkin' | 'chat'
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [onboardingDone, setOnboardingDone] = useState(false);
  const [onboardingDeferred, setOnboardingDeferred] = useState(false);
  const [dismissedContinue, setDismissedContinue] = useState(false);
  const [voiceHintDismissed, setVoiceHintDismissed] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatStarted = useRef(false);

  const voice = useVoiceConversation({
    enabled: voiceEnabled,
    defaultOn: voiceDefaultOn,
    onTranscribed: (text) => handleSendWithText(text),
    voiceId,
    onVoiceModeChange: (next) => { setVoiceDefaultOn(next).catch(() => {}); },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Start or resume the onboarding session. If a prior session is resumed
  // with persisted messages, hydrate them, skip the StateCheckIn phase, and
  // mark chat as started so the "I'm ready" auto-send doesn't re-fire.
  const initSession = useCallback(async () => {
    setSessionLoading(true);
    try {
      const res = await startOnboarding();
      if (res.already_completed) {
        onComplete?.();
        return;
      }
      if (res.session_id) {
        setSessionId(res.session_id);
        if (Array.isArray(res.messages) && res.messages.length > 0) {
          setMessages(res.messages);
          chatStarted.current = true;
          setPhase('chat');
        }
      }
    } catch (err) {
      console.error('Failed to start onboarding:', err);
      setError('Failed to start onboarding. Please refresh and try again.');
    } finally {
      setSessionLoading(false);
    }
  }, [onComplete]);

  // Probe for a resumable session on mount so a returning user lands directly
  // back in their chat instead of being forced through StateCheckIn again.
  useEffect(() => {
    initSession();
  }, [initSession]);

  useEffect(() => { trackAuthed('onboarding_start', {}); }, []);

  const handleCheckInComplete = useCallback((scores) => {
    setPhase('chat');
  }, []);

  const handleCheckInSkip = useCallback(() => {
    setPhase('chat');
  }, []);

  // Auto-send opening message once session is ready
  useEffect(() => {
    if (sessionId && phase === 'chat' && !chatStarted.current) {
      chatStarted.current = true;
      handleSendWithText("I'm ready to get started.");
    }
  }, [sessionId, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendWithText = async (text) => {
    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      await handleSendStreaming(updatedMessages);
    } catch {
      try {
        await handleSendNonStreaming(updatedMessages);
      } catch {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Connection error. Please try again.',
          timestamp: new Date().toISOString()
        }]);
      }
    } finally {
      setLoading(false);
      setStreaming(false);
    }
  };

  const handleSendStreaming = async (updatedMessages) => {
    let assistantText = '';
    const assistantMsg = { role: 'assistant', content: '', timestamp: new Date().toISOString(), thinking: true };
    setMessages([...updatedMessages, assistantMsg]);
    setStreaming(true);
    voice.beginUtterance();

    let onboardingComplete = false;
    let deferredClose = false;

    await sendMessageStream(updatedMessages, sessionId, (event) => {
      switch (event.type) {
        case 'text_delta':
          assistantText += event.text;
          voice.feedTextDelta(event.text);
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], content: assistantText, thinking: false };
            return updated;
          });
          break;
        case 'tool_start':
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === 'assistant' && !last.content) {
              updated[updated.length - 1] = { ...last, thinking: true, toolName: event.name };
            }
            return updated;
          });
          break;
        case 'tool_result':
          if (event.name === 'complete_onboarding' && !event.is_error) {
            onboardingComplete = true;
            deferredClose = !!event.result?.deferred;
          }
          break;
        case 'done':
          if (onboardingComplete) {
            setOnboardingDeferred(deferredClose);
            setOnboardingDone(true);
            trackAuthed('onboarding_complete', { deferred: !!deferredClose, path: 'stream' });
          }
          break;
        case 'error':
          assistantText += (assistantText ? '\n\n' : '') + `Error: ${event.message}`;
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], content: assistantText };
            return updated;
          });
          break;
        default:
          break;
      }
    });

    voice.endUtterance();
  };

  const handleSendNonStreaming = async (updatedMessages) => {
    const result = await sendMessage(updatedMessages, sessionId);
    if (result.response) {
      setMessages([...updatedMessages, {
        role: 'assistant',
        content: result.response,
        timestamp: new Date().toISOString()
      }]);
    }
    // Mirror the streaming path's complete_onboarding detection — the backend
    // sets this when the fallback turn completed onboarding.
    if (result.onboarding_complete) {
      setOnboardingDeferred(!!result.onboarding_deferred);
      setOnboardingDone(true);
      trackAuthed('onboarding_complete', { deferred: !!result.onboarding_deferred, path: 'fallback' });
    }
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text || loading) return;
    handleSendWithText(text);
  };

  const autoGrow = useCallback((el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Phase 0: Welcome / expectations
  if (phase === 'intro') {
    return (
      <div className="h-full overflow-auto">
        <div className="max-w-xl mx-auto px-4 py-10 animate-fade-in">
          <div className="text-center mb-6">
            <Compass size={36} className="text-blue-400 mx-auto mb-3" />
            <h1 className="text-xl font-display font-semibold tracking-tight text-slate-200">Welcome to The Navigator</h1>
            <p className="text-sm text-slate-400 mt-2">The mentor who remembers your word — and won't let you off the hook.</p>
          </div>

          <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-5 space-y-4 text-sm text-slate-300 leading-relaxed">
            <p>
              Before we get to daily planning, we'll spend about <span className="text-slate-200 font-medium">5 minutes</span> getting to know each other. This shapes how the Navigator shows up for you — the questions it asks, the way it pushes, what it remembers.
            </p>
            <p>
              Two short steps:
            </p>
            <ol className="space-y-1.5 pl-5 list-decimal marker:text-slate-500">
              <li>A quick check-in on how you're showing up today.</li>
              <li>A short conversation so the Navigator can learn who you are and what you're working on.</li>
            </ol>
            <p className="text-xs text-slate-500 pt-1">
              Skim the questions, answer honestly. There are no wrong answers, and you can always refine things later.
              When you finish, your first Deep Rest is made from what you told it.
              {' '}<button type="button" onClick={() => hints?.openVideo()} className="text-blue-400 hover:text-blue-300">Prefer to watch first? Two and a half minutes.</button>
            </p>
          </div>

          <button
            onClick={() => setPhase('checkin')}
            className="w-full mt-6 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg py-3 min-h-tap transition-colors"
          >
            Let's begin
          </button>
        </div>
      </div>
    );
  }

  // Phase 1: StateCheckIn
  if (phase === 'checkin') {
    return (
      <div className="h-full overflow-auto">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="text-center mb-6">
            <Compass size={28} className="text-blue-400 mx-auto mb-2" />
            <h1 className="text-lg font-semibold text-slate-200">Step 1 of 2 — Quick check-in</h1>
            <p className="text-sm text-slate-500 mt-1">How are you showing up today?</p>
          </div>
          <StateCheckIn onComplete={handleCheckInComplete} onSkip={handleCheckInSkip} />
        </div>
      </div>
    );
  }

  // Phase 2: Chat (waiting for session)
  if (sessionLoading || (!sessionId && !error)) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader2 size={24} className="animate-spin text-blue-400 mb-3" />
        <p className="text-sm text-slate-400">Setting things up...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-4">
        <p className="text-sm text-red-400 text-center">{error}</p>
      </div>
    );
  }

  // Phase 2: Streaming chat
  return (
    <div className="flex flex-col h-full max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-800 bg-blue-950/20">
        <Compass size={16} className="text-blue-400" />
        <span className="text-sm font-medium text-blue-400">Getting to Know You</span>
        {voice.available && (
          <button
            onClick={() => { setVoiceHintDismissed(true); voice.toggleVoiceMode(); }}
            title={voice.voiceMode ? 'Voice mode on — tap to turn off' : 'Talk instead of typing'}
            aria-pressed={voice.voiceMode}
            className={`ml-auto flex items-center gap-1.5 min-h-tap sm:min-h-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              voice.voiceMode
                ? 'text-purple-200 bg-purple-900/40 border-purple-600/50 hover:bg-purple-900/60'
                : 'text-purple-300 bg-purple-900/20 border-purple-700/40 hover:bg-purple-900/40 animate-pulse'
            }`}
          >
            {voice.voiceMode ? <Volume2 size={15} /> : <VolumeX size={15} />}
            <span>{voice.voiceMode ? 'Voice on' : 'Try voice'}</span>
          </button>
        )}
      </div>

      {/* First-turn nudge: onboarding voice is off by default (iOS autoplay
          safety), so point new users at the toggle. Dismisses on tap, on
          turning voice on, or after their first reply. */}
      {voice.available && !voice.voiceMode && !voiceHintDismissed &&
        messages.filter((m) => m.role === 'user').length < 1 && (
        <div className="px-4 py-2 bg-purple-950/30 border-b border-purple-900/40 text-xs text-purple-200 flex items-center justify-between gap-3">
          <span>🔊 Prefer to talk? Tap <span className="font-medium">Try voice</span> above to speak your answers instead of typing.</span>
          <button
            onClick={() => setVoiceHintDismissed(true)}
            className="text-purple-400 hover:text-purple-200 shrink-0"
            aria-label="Dismiss"
          >×</button>
        </div>
      )}

      {voice.error && (
        <div className="px-4 py-2 bg-red-900/20 border-b border-red-900/40 text-xs text-red-300 flex items-center justify-between">
          <span>{voice.error}</span>
          <button onClick={voice.clearError} className="text-red-400 hover:text-red-200" aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 pt-4 space-y-4">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-lg px-4 py-3 text-sm leading-relaxed ${
              msg.role === 'user'
                ? 'bg-blue-600/20 text-blue-100 border border-blue-800/30'
                : 'bg-slate-800/50 text-slate-200 border border-slate-700/50'
            }`}>
              {msg.thinking && !msg.content ? (
                <div className="flex items-center gap-2 text-slate-400">
                  <Loader2 size={14} className="animate-spin" />
                  <span className="text-xs">
                    {msg.toolName ? `Running ${msg.toolName}...` : 'Thinking...'}
                  </span>
                </div>
              ) : (
                <div className="whitespace-pre-wrap">{msg.content}</div>
              )}
            </div>
          </div>
        ))}

        {loading && !streaming && (
          <div className="flex justify-start">
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3">
              <Loader2 size={16} className="animate-spin text-slate-400" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input / Completion prompt */}
      <div className="border-t border-slate-800 p-4">
        {onboardingDone ? (
          <div className="max-w-3xl mx-auto">
            {onboardingDeferred ? (
              <div className="text-center space-y-3">
                <p className="text-sm text-slate-400">
                  All set — we've saved what you shared. Come back anytime and we'll pick up where we left off.
                </p>
                <button
                  onClick={() => onComplete?.()}
                  className="bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm font-medium rounded-lg px-6 py-2.5 transition-colors"
                >
                  Continue
                </button>
              </div>
            ) : dismissedContinue ? (
              <div className="text-center space-y-3">
                <p className="text-sm text-slate-400">
                  Whenever you're ready — daily planning is your next step.
                </p>
                <button
                  onClick={() => onComplete?.()}
                  className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg px-6 py-2.5 transition-colors"
                >
                  Start today's plan
                </button>
              </div>
            ) : (
              <div className="text-center space-y-2">
                <p className="text-sm text-slate-200 font-medium">You're set up.</p>
                {/* The first Deep Rest is queued server-side by complete_onboarding;
                    the card polls until it is ready. Ends the session on a high,
                    not a price. */}
                <DeepRestCard source="onboarding" className="text-left my-3" />
                <p className="text-xs text-slate-400 mb-2">
                  {freePlanningDays
                    ? `Your first ${freePlanningDays} days of daily planning are on us. About 5 minutes each morning: a quick map of what matters today.`
                    : 'Daily planning takes about 5 minutes — a quick map of what matters today.'}
                </p>
                <div className="flex justify-center gap-3 pt-1">
                  <button
                    onClick={() => onComplete?.()}
                    className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg px-6 py-2.5 transition-colors"
                  >
                    Start today's plan
                  </button>
                  <button
                    onClick={() => setDismissedContinue(true)}
                    className="bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm font-medium rounded-lg px-6 py-2.5 transition-colors"
                  >
                    Not yet
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : voice.voiceMode ? (
          <VoiceInput voice={voice} loading={loading} />
        ) : (
          <div className="flex gap-2 max-w-3xl mx-auto">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => { setInput(e.target.value); autoGrow(e.target); }}
              onKeyDown={handleKeyDown}
              placeholder="Type your response..."
              rows={1}
              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder-slate-500 resize-none overflow-hidden focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg px-4 py-3 transition-colors"
            >
              <Send size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
